import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateUser } from '../../../store/slices/userSlice';
import {
  Modal, Form, Row, Col, Switch,
  CustomInput, CustomSelect, Colors, Constants
} from '../../../utils/commonImports';
import { Button } from 'antd';

const EditUserModal = ({ isOpen, onClose, user }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.user);
  const [form] = Form.useForm();
  
  useEffect(() => {
    if (user && isOpen) {
      form.setFieldsValue({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        contactNumber: user.contactNumber,
        role: user.role,
        can_approve: user.can_approve || false
      });
    }
  }, [user, isOpen, form]);

  const handleSubmit = (values) => {
    const updateData = {};
    
    // Only include fields that have values
    if (values.firstName) updateData.firstName = values.firstName;
    if (values.lastName) updateData.lastName = values.lastName;
    if (values.contactNumber) updateData.contactNumber = values.contactNumber;
    if (values.role) updateData.role = values.role;
    if (values.password) updateData.password = values.password;
    // can_approve can be false, so we need to explicitly check if it exists
    if (values.hasOwnProperty('can_approve')) {
      updateData.can_approve = values.can_approve;
    }
    
    dispatch(updateUser({ id: user._id, userData: updateData }));
    form.resetFields();
    onClose();
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      form.resetFields();
    }
  }, [isOpen, form]);

  return (
    <Modal
      title="Edit User"
      open={isOpen}
      onCancel={handleCancel}
      footer={null}
      width={500}
      centered
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        style={{ marginTop: '20px' }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true, message: 'First name is required' }]}
            >
              <CustomInput placeholder="Enter first name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{ required: true, message: 'Last name is required' }]}
            >
              <CustomInput placeholder="Enter last name" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Contact Number"
              name="contactNumber"
              rules={[{ required: true, message: 'Contact number is required' }]}
            >
              <CustomInput placeholder="Enter contact number" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Role"
              name="role"
            >
              <CustomSelect 
                placeholder="Select role"
                options={[
                  { value: Constants.ROLES.ADMIN, label: 'Admin' },
                  { value: Constants.ROLES.USER, label: 'User' }
                ]}
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.role !== currentValues.role}
        >
          {({ getFieldValue }) => {
            const role = getFieldValue('role');
            if (role === Constants.ROLES.ADMIN) {
              return (
                <Form.Item
                  name="can_approve"
                  label="Can Approve Transactions"
                  valuePropName="checked"
                >
                  <Switch 
                    checkedChildren="Yes" 
                    unCheckedChildren="No"
                  />
                </Form.Item>
              );
            }
            return null;
          }}
        </Form.Item>

        <Form.Item
          label="Email"
          name="email"
        >
          <CustomInput disabled style={{ backgroundColor: '#f5f5f5' }} />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="New Password"
              name="password"
            >
              <CustomInput type="password" placeholder="Enter new password" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Confirm Password"
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const password = getFieldValue('password');
                    if (password && !value) {
                      return Promise.reject(new Error('Please confirm your password'));
                    }
                    if (value && password !== value) {
                      return Promise.reject(new Error('Passwords do not match!'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <CustomInput type="password" placeholder="Confirm new password" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Button onClick={handleCancel} style={{ marginRight: '8px' }}>
            Cancel
          </Button>
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={loading}
            style={{
              backgroundColor: Colors.primary,
              borderColor: Colors.primary
            }}
          >
            Update User
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditUserModal;